import { useEffect, useRef } from "react";

/**
 * The workspace's shared 472px push panel — the comp's #thread-source /
 * #snapshot-flyover shell. It sits beside the main column (which narrows to make
 * room, never a modal, never a standing third column) and is dismissible with
 * Esc or the content's own close control, restoring focus to the trigger.
 *
 * The shell owns only the chrome and the open/Esc/focus lifecycle; the panel
 * body (a scorecard, a form snapshot, …) is passed as children. `openKey`
 * identifies the currently-shown item: null closes the panel; changing it while
 * open re-runs focus capture/restore so switching sources behaves the same as a
 * fresh open. Extracted from SourceFlyover so both the scorecard flyover and the
 * form-snapshot flyover share one shell.
 */
export function FlyoverShell({
  openKey,
  onClose,
  ariaLabel = "Source flyover",
  children,
}: {
  openKey: string | null;
  onClose: () => void;
  ariaLabel?: string;
  children?: React.ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!openKey) return;

    restoreFocusRef.current = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();

    return () => {
      restoreFocusRef.current?.focus?.();
    };
  }, [openKey]);

  useEffect(() => {
    if (!openKey) return;

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }

    document.addEventListener("keydown", onKeyDown);

    return () => document.removeEventListener("keydown", onKeyDown);
  }, [openKey, onClose]);

  if (!openKey) return null;

  return (
    <aside
      ref={panelRef}
      tabIndex={-1}
      role="complementary"
      aria-label={ariaLabel}
      className="z-[5] flex h-full w-[472px] max-w-full shrink-0 flex-col border-l border-border bg-card shadow-[-12px_0_28px_rgba(40,3,15,0.08)] outline-none max-lg:absolute max-lg:right-0 max-lg:top-0"
    >
      {children}
    </aside>
  );
}
